import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import NavHeader from './NavHeader';
import Loader from './Loader';

// Define animations first
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 10px rgba(0, 255, 0, 0.3); }
  50% { box-shadow: 0 0 20px rgba(0, 255, 0, 0.5); }
`;

const typing = keyframes`
  from { width: 0 }
  to { width: 100% }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const rotate16 = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const PageContainer = styled.div`
  position: relative;
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;
`;

const Canvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
`;

const ResumeContainer = styled.div`
  display: flex;
  flex-direction: column;
  color: #00ff00;
  font-family: 'Courier New', monospace;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  padding: 2rem;
  z-index: 1;
  width: 95%;
  max-width: 1200px;
  margin: 2rem;

  @media (max-width: 768px) {
    width: 95%;
    padding: 1rem;
    margin: 1rem;
  }
`;

const Section = styled.div`
  border: 1px solid #00ff00;
  border-radius: 10px;
  padding: 2rem;
  margin-bottom: 2rem;
  width: 100%;
  margin-top: 0;
  margin-left: 0;
`;

const SectionTitle = styled.h3`
  font-size: 2.5rem;
  color: #00ff00;
  text-align: center;
  margin-bottom: 2rem;
  position: relative;
  
  span {
    display: inline-block;
    margin-right: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover span {
    transform: scale(1.2);
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, transparent, #00ff00, transparent);
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    animation: ${glowPulse} 2s infinite;
  }
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const SkillCategory = styled.div`
  h5 {
    font-size: 1.5rem;
    color: #00ff00;
    margin-bottom: 1rem;
    transition: text-shadow 0.3s ease;
  }
  
  &:hover h5 {
    text-shadow: 0 0 10px rgba(0, 255, 0, 0.6);
  }
  
  ul {
    list-style: none;
    padding-left: 1rem;
  }
  
  li {
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
    padding-left: 1rem;
    position: relative;
    transition: transform 0.2s ease, color 0.2s ease;
    
    &:before {
      position: absolute;
      left: 0;
      color: #00ff00;
      content: ">";
      opacity: 0;
      transition: opacity 0.2s ease;
    }
    
    &:hover {
      transform: translateX(5px);
      color: #00ff99;
      
      &:before {
        opacity: 1;
      }
    }
  }
`;

const ExperienceItem = styled.div`
  margin-bottom: 2rem;

  h5 {
    font-size: 1.8rem;
    color: #00ff00;
    margin-bottom: 0.5rem;
    margin-top: -1rem;
  }

  h3 {
    font-size: 1.5rem;
    color: #00ff00;
    margin-bottom: 1rem;
  }

  h4 {
    font-size: 1.3rem;
    color: #00ff00;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 1.2rem;
    margin-bottom: 1rem;
  }

  .degree {
    margin-bottom: 1.5rem;
    
    div {
      font-size: 1.2rem;
      margin-bottom: 0.5rem;
    }
  }

  .coursework {
    margin-top: 1rem;
  }

  .course-section {
    margin-bottom: 1rem;
  }
`;

const horizontalScrollStyle = {
  overflowX: 'auto',
  whiteSpace: 'nowrap',
  padding: '1rem 0',
};

const horizontalScrollUlStyle = {
  display: 'inline-block',
  padding: '0',
  margin: '0',
};

const horizontalScrollLiStyle = {
  display: 'inline',
  marginRight: '1rem',
};

// Timeline Components
const TimelineContainer = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 2rem auto;
  
  &::after {
    content: '';
    position: absolute;
    width: 6px;
    background-color: #00ff00;
    top: 0;
    bottom: 0;
    left: 50%;
    margin-left: -3px;
    box-shadow: 0 0 15px rgba(0, 255, 0, 0.5);
    z-index: 1;
  }
  
  @media screen and (max-width: 768px) {
    &::after {
      left: 31px;
    }
  }
`;

const TimelineItem = styled.div`
  padding: 10px 20px;
  position: relative;
  width: 50%;
  box-sizing: border-box;
  opacity: 0;
  transform: translateX(${props => props.position === 'left' ? '-50px' : '50px'});
  animation: slideIn 0.8s forwards ${props => props.delay}s;
  
  &:nth-child(odd) {
    left: 0;
    padding-right: 60px;
  }
  
  &:nth-child(even) {
    left: 50%;
    padding-left: 60px;
  }
  
  @keyframes slideIn {
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @media screen and (max-width: 768px) {
    width: 100%;
    padding-left: 90px;
    padding-right: 25px;
    
    &:nth-child(odd), &:nth-child(even) {
      left: 0;
    }
  }
`;

const TimelineBadge = styled.div`
  position: absolute;
  top: 16px;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  text-align: center;
  z-index: 200;
  box-shadow: 0 0 0 4px #00ff00, inset 0 2px 0 rgba(0, 255, 0, 0.08), 0 3px 0 4px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: black;
  overflow: hidden;
  
  ${props => props.position === 'left' ? `
    right: -40px;
    left: auto;
  ` : `
    left: -40px;
    right: auto;
  `}
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: black;
    border-radius: 50%;
  }
  
  img {
    width: 85%;
    height: 85%;
    object-fit: contain;
    border-radius: 50%;
    position: relative;
    z-index: 201;
    border: 2px solid #00ff00;
  }
  
  @media screen and (max-width: 768px) {
    width: 60px;
    height: 60px;
    left: -30px;
    
    img {
      width: 80%;
      height: 80%;
    }
  }
`;

const TimelineContent = styled.div`
  padding: 20px 30px;
  background: rgba(0, 20, 0, 0.8);
  position: relative;
  border-radius: 6px;
  border: 1px solid #00ff00;
  backdrop-filter: blur(5px);
  box-shadow: 0 0 20px rgba(0, 255, 0, 0.2);
  transition: all 0.3s ease;
  min-height: 300px;
  width: 100%;
  
  &:hover {
    box-shadow: 0 0 30px rgba(0, 255, 0, 0.4);
    transform: translateY(-5px);
    border-color: #00ff99;
    background: rgba(0, 25, 0, 0.8);
  }
  
  h3 {
    margin-top: 0;
    color: #00ff00;
    font-size: 1.8rem;
    letter-spacing: 1px;
    margin-bottom: 0.5rem;
    text-shadow: 0 0 10px rgba(0, 255, 0, 0.3);
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      width: 0;
      height: 2px;
      bottom: -2px;
      left: 0;
      background-color: #00ff00;
      transition: width 0.3s ease;
    }
  }
  
  &:hover h3::after {
    width: 100%;
  }
  
  li {
    margin-bottom: 10px;
    position: relative;
    line-height: 1.6;
    transition: transform 0.2s ease;
    
    &:hover {
      transform: translateX(5px);
      color: #00ff99;
    }
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 25px;
    height: 25px;
    background-color: rgba(0, 0, 0, 0.8);
    border: 4px solid #00ff00;
    top: 15px;
    z-index: 2;
    border-radius: 50%;
    box-shadow: 0 0 10px rgba(0, 255, 0, 0.5);
    transition: all 0.3s ease;
    
    ${props => props.position === 'left' ? `
      right: -17px;
      left: auto;
    ` : `
      left: -17px;
      right: auto;
    `}
  }
`;

const SkillBar = styled.div`
  margin-bottom: 1.5rem;
  
  .skill-name {
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
    display: flex;
    justify-content: space-between;
    
    span:last-child {
      color: #00aa00;
    }
  }
  
  .skill-bar {
    height: 10px;
    background: rgba(0, 50, 0, 0.2);
    border-radius: 5px;
    position: relative;
    overflow: hidden;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: ${props => props.percentage}%;
      background: linear-gradient(90deg, #004400, #00ff00);
      border-radius: 5px;
      animation: fillBar 1.5s ease-out forwards;
      transform-origin: left;
      transform: scaleX(0);
    }
    
    @keyframes fillBar {
      to { transform: scaleX(1); }
    }
  }
`;

// ScrollObserver component definition
const ScrollObserver = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(elementRef.current);
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={elementRef}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 0.5s ease, transform 0.5s ease',
      }}
    >
      {children}
    </div>
  );
};

// Add these new styled components for the enhanced skills section
const SkillsWrapper = styled.div`
  margin: 2rem 0;
  position: relative;
  background: rgba(0, 10, 0, 0.2);
  border-radius: 10px;
  padding: 1rem;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, rgba(0, 30, 0, 0.3) 0%, rgba(0, 0, 0, 0.5) 70%);
    pointer-events: none;
    z-index: 0;
    border-radius: 10px;
  }
`;

const SkillCategoryTitle = styled.h4`
  font-size: 1.8rem;
  color: #00ff00;
  margin: 2.5rem 0 2rem;
  padding-bottom: 0.5rem;
  text-shadow: 0 0 10px rgba(0, 255, 0, 0.7);
  text-align: center;
  position: relative;
  overflow: hidden;
  font-weight: bold;
  letter-spacing: 1px;
  
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, #00ff00, transparent);
    animation: ${shimmer} 3s infinite linear;
    background-size: 200% 100%;
  }
`;

const SkillsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 2rem;
  position: relative;
  perspective: 1000px;
`;

const SkillCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  border: 1px solid #00ff00;
  border-radius: 10px;
  background: rgba(0, 25, 0, 0.4);
  width: 120px;
  height: 150px;
  transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  z-index: 2;
  transform-style: preserve-3d;
  animation: ${float} ${props => 3 + Math.random() * 2}s infinite ease-in-out;
  animation-delay: ${props => Math.random() * 2}s;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, #00ff00, transparent, #00ff00, transparent);
    z-index: -1;
    border-radius: 12px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-10px) rotateY(10deg) scale(1.05);
    box-shadow: 0 15px 25px rgba(0, 255, 0, 0.2);
    background: rgba(0, 45, 0, 0.5);
    border-color: #00ff99;
    
    &::before {
      opacity: 1;
      animation: ${rotate} 3s linear infinite;
    }
  }
  
  img {
    width: 68px;
    height: 68px;
    object-fit: contain;
    margin-bottom: 1rem;
    filter: drop-shadow(0 0 8px rgba(0, 255, 0, 0.4));
    transition: all 0.4s ease;
    transform-style: preserve-3d;
    backface-visibility: hidden;
  }
  
  &:hover img {
    transform: scale(1.15) translateZ(20px);
    filter: drop-shadow(0 0 15px rgba(0, 255, 0, 0.7));
  }
  
  span {
    font-size: 1rem;
    text-align: center;
    color: #00ff00;
    text-shadow: 0 0 5px rgba(0, 255, 0, 0.5);
    transition: all 0.3s ease;
    transform-origin: center;
  }
  
  &:hover span {
    color: #00ff99;
    transform: scale(1.1);
    text-shadow: 0 0 10px rgba(0, 255, 0, 0.8);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 10%;
    width: 80%;
    height: 10px;
    background: radial-gradient(ellipse at center, rgba(0, 255, 0, 0.3) 0%, transparent 70%);
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.3s ease;
    filter: blur(5px);
  }
  
  &:hover::after {
    opacity: 1;
  }
`;

// Add this component for staggered animation when scrolling
const StaggerContainer = ({ children, staggerDelay = 0.1 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(containerRef.current);
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  return (
    <div ref={containerRef}>
      {React.Children.map(children, (child, index) => (
        <div
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
            transition: `opacity 0.5s ease, transform 0.5s ease`,
            transitionDelay: `${index * staggerDelay}s`
          }}
        >
          {child}
        </div>
      ))}
    </div>
  );
};

// Add this matrix effect component for the skills section background
const MatrixEffect = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 0;
  opacity: 0.08;
  
  &::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    background: url('/matrix-code.png') repeat;
    animation: ${pulse} 30s infinite linear;
    opacity: 0.2;
  }
`;

const Resume = () => {
  const canvasRef = useRef(null);
  const [showLoader, setShowLoader] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    handleResize();
    
    const columns = Math.floor(window.innerWidth / 10);
    const drops = Array(columns).fill(1);

    const draw = () => {
      context.fillStyle = 'rgba(0, 0, 0, 0.05)';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = '#00ff00';
      context.font = '10px monospace';

      drops.forEach((y, index) => {
        const text = String.fromCharCode(Math.floor(Math.random() * 128));
        const x = index * 10;
        context.fillText(text, x, y * 10);

        if (y * 10 > canvas.height && Math.random() > 0.975) {
          drops[index] = 0;
        }
        drops[index]++;
      });
    };

    window.addEventListener('resize', handleResize);
    
    const interval = setInterval(draw, 50);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (showLoader) {
      const timer = setTimeout(() => {
        setShowLoader(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [showLoader]);

  return (
    <PageContainer>
      <Canvas ref={canvasRef} />
      {showLoader ? (
        <Loader />
      ) : (
        <>
          <NavHeader title="Resume" />
          <ResumeContainer>
            <ScrollObserver>
              <SectionTitle>
                <span role="img" aria-label="education">🎓</span> Education
              </SectionTitle>
              
              <TimelineContainer>
                <TimelineItem position="left" delay={0.3}>
                  <TimelineBadge position="right">
                    <img src="/NYU.jpg" alt="NYU Logo" />
                  </TimelineBadge>
                  <TimelineContent position="right">
                    <h3>New York University</h3>
                    <h4>Tandon School of Engineering</h4>
                    <div className="date">2021 - 2023</div>
                    <div className="degree"><strong>Major:</strong> Computer Engineering</div>
                    <div className="degree"><strong>Minor:</strong> Science & Technology</div>
                    <div className="honors"><strong>Honors:</strong> Founder's Day Award | Cum Laude | Dean's List</div>
                    <div className="gpa"><strong>GPA:</strong> 3.8</div>
                    
                    <h4>Relevant Coursework</h4>
                    <ul>
                      <li>Computer Architecture | Machine Learning | Operating Systems | Databases | Digital Logic & Machine Design</li>
                      <li>Real-Time Embedded Systems (Grad) | Embedded Systems | VLSI Circuits | Fundamentals of Electronics</li>
                      <li> Advance Linear Algebra | Complex Variables | Discrete Math | Data Analysis</li>
                    </ul>
                  </TimelineContent>
                </TimelineItem>
                
                <TimelineItem position="left" delay={0.5}>
                  <TimelineBadge position="left">
                    <img src="/BK.png" alt="Brooklyn College Logo" />
                  </TimelineBadge>
                  <TimelineContent position="left">
                    <h3>Brooklyn College</h3>
                    <h4>Department of Engineering</h4>
                    <div className="date">2019 - 2021</div>
                    <div className="degree"><strong>Major:</strong> Computer Engineering</div>
                    <div className="gpa"><strong>GPA:</strong> 3.7</div>
                    
                    <h4>Relevant Coursework</h4>
                    <ul>
                      <li>Intro Programming (Java) | Object Orentied Programming | Data Structures | Algorithms | Paradigms in C++ </li>
                      <li> Engineering Physics I-II | Electric Circuits | Electrical Measurements Lab | Engineering Mechanics </li>
                      <li>Calculus I-III | Linear Algebra | Differential Equations </li>
                    </ul>
                  </TimelineContent>
                </TimelineItem>
              </TimelineContainer>
            </ScrollObserver>
            
            <ScrollObserver>
              <Section>
                <SectionTitle>Technical Skills</SectionTitle>
                <SkillsWrapper>
                  <MatrixEffect />
                  
                  <StaggerContainer>
                    <SkillCategoryTitle>Programming Languages</SkillCategoryTitle>
                    <SkillsRow>
                      <SkillCard>
                        <img src="/python.webp" alt="Python" />
                        <span>Python</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/java.png" alt="Java" />
                        <span>Java</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/c++.png" alt="C++" />
                        <span>C++</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/x86.png" alt="Assembly" />
                        <span>Assembly</span>
                      </SkillCard>
                    </SkillsRow>
                  </StaggerContainer>
                  
                  <StaggerContainer>
                    <SkillCategoryTitle>Machine Learning Frameworks</SkillCategoryTitle>
                    <SkillsRow>
                      <SkillCard>
                        <img src="/tensorflow.png" alt="TensorFlow" />
                        <span>TensorFlow</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/pytorch.png" alt="PyTorch" />
                        <span>PyTorch</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/keras.png" alt="PyTorch" />
                        <span>keras</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/SK.png" alt="Scikit-learn" />
                        <span>Scikit-learn</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/opencv.png" alt="OpenCV" />
                        <span>OpenCV</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/yolo.jpg" alt="Pandas" />
                        <span>YOLO</span>
                      </SkillCard>
                    </SkillsRow>
                  </StaggerContainer>
                  
                  <StaggerContainer>
                    <SkillCategoryTitle>Web Development</SkillCategoryTitle>
                    <SkillsRow>
                      <SkillCard>
                        <img src="/react.png" alt="React" />
                        <span>React</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/Flask.png" alt="Node.js" />
                        <span>Flask</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/HTML.png" alt="HTML5" />
                        <span>HTML5</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/CSS.png" alt="CSS3" />
                        <span>CSS3</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/sql.png" alt="SQL" />
                        <span>SQL</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/NoSQL.png" alt="SQL" />
                        <span>NoSQL</span>
                      </SkillCard>
                
                    </SkillsRow>
                  </StaggerContainer>
                  
                  <StaggerContainer>
                    <SkillCategoryTitle>Embedded Systems</SkillCategoryTitle>
                    <SkillsRow>
                      <SkillCard>
                        <img src="/Arduino.png" alt="Arduino" />
                        <span>Arduino</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/stm32.png" alt="STM32" />
                        <span>STM32</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/mbed.png" alt="STM32" />
                        <span>Mbed Framework</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/fpga.png" alt="FPGA" />
                        <span>FPGA</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/Verlog.jpg" alt="Verilog" />
                        <span>Verilog</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/Esp32.jpg" alt="ESP32" />
                        <span>ESP32</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/Resp.jpg" alt="ESP32" />
                        <span>Raspberry Pi</span>
                      </SkillCard>
                    </SkillsRow>
                  </StaggerContainer>
                  
                  <StaggerContainer>
                    <SkillCategoryTitle>Tools & Technologies</SkillCategoryTitle>
                    <SkillsRow>
                      <SkillCard>
                        <img src="/git.png" alt="Git" />
                        <span>Git/GitHub</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/UML.png" alt="UMLs" />
                        <span>UML</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/virtuoso.png" alt="Docker" />
                        <span>virtuoso</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/firebase.png" alt="SQL" />
                        <span>Firebase</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/modelsim.jpg" alt="Modelsim" />
                        <span>modelsim</span>
                      </SkillCard>
                      <SkillCard>
                        <img src="/fusion360.png" alt="F" />
                        <span>Fusion 360</span>
                      </SkillCard>
                    </SkillsRow>
                  </StaggerContainer>
                </SkillsWrapper>
              </Section>
            </ScrollObserver>
            
            <ScrollObserver>
              <SectionTitle>
                <span role="img" aria-label="experience">💼</span> Experience
              </SectionTitle>
              
              <TimelineContainer>
                <TimelineItem position="right" delay={0.5}>
                  <TimelineBadge position="right">
                    <img src="/PepsiCo.jpg" alt="PepsiCo Logo" />
                  </TimelineBadge>
                  <TimelineContent position="right">
                    <h3>R&D Engineer</h3>
                    <h4>PepsiCo</h4>
                    <div className="date">July 2023 - Present</div>
                    <ul>
                      <li>Collaborated with the Equipment Research and Development team to transform traditional vending machines into IoT-enabled smart vending machines, incorporating computer vision and deep learning for dynamic product recognition.</li>
                      <li>Implemented a custom payment solution using QR code-based payments with UPI and digital wallets, as well as integrated with third-party payment providers such as Nayax and Pyater to offer a variety of payment options.</li>
                      <li>Pioneered the integration of a novel computer vision feature using YOLOv8, OpenCV, and Stereovision technologies, enabling accurate real-time tracking and size calculation of products independent of distance, without the need for reference objects.</li>
                      <li>Developed a computer vision-based door status detection algorithm using depth-based stereo vision techniques to reduce hardware costs and improve system efficiency and security.</li>
                      <li>Improved vending machine security by designing and implementing a fail-safe locking mechanism, including wire schematics and firmware development in C++, integrating a device with the lock to shift it from a fail-neutral to a fail-safe state during power outages.</li>
                    </ul>
                  </TimelineContent>
                </TimelineItem>
                
                <TimelineItem position="left" delay={0.7}>
                  <TimelineBadge position="left">
                    <img src="/SIIL.png" alt="SIIL Logo" />
                  </TimelineBadge>
                  <TimelineContent position="left">
                    <h3>Embedded Software Engineer</h3>
                    <h4>Student Impact Innovation Lab</h4>
                    <div className="date">Sep 2022 - June 2023</div>
                    <ul>
                      <li>Enhanced accuracy and efficiency in skin cancer screening by developing a handheld device that utilizes Electrical Impedance Spectroscopy (EIS) for accurate and efficient non-invasive detection of skin abnormalities</li>
                      <li>Devised a system within device to measure skin impedance, detect abnormalities, and relay data to a web application</li>
                      <li>Handled frequency sweep procedures and complex calculations of impedance magnitude and phase to ensure accurate detection of skin tissue abnormalities</li>
                    </ul>
                  </TimelineContent>
                </TimelineItem>
                
                <TimelineItem position="right" delay={0.9}>
                  <TimelineBadge position="right">
                    <img src="wearable.png" alt="VIP Logo" />
                  </TimelineBadge>
                  <TimelineContent position="right">
                    <h3>Embedded Software Engineer</h3>
                    <h4>Vertical Integrated Projects at NYU</h4>
                    <div className="date">Jun 2022 - Aug 2022</div>
                    <ul>
                      <li>Collaborated with cross-functional teams to drive the development of a wearable device for prosthetics. The device, utilizing non-invasive muscle signal decoding, aimed to enhance quality of life for individuals with physical or neurological impairments</li>
                      <li>Used surface Mechanomyography (sMMG), leveraging the differential readings from accelerometers, to capture detailed muscle signals. This technique was essential for predicting hand movements and providing immediate, real-time feedback to users</li>
                      <li>Boosted wireless transmission rates by 3900%, from 50Hz to 2000Hz, through the strategic implementation of the User Datagram Protocol(UDP) and seamless integration with IoT devices. This enhancement facilitated faster and more efficient communication for the prosthetic device</li>
                    </ul>
                  </TimelineContent>
                </TimelineItem>
                
                <TimelineItem position="left" delay={1.1}>
                  <TimelineBadge position="left">
                    <img src="Madison.jpeg" alt="Madison Jewelers Logo" />
                  </TimelineBadge>
                  <TimelineContent position="left">
                    <h3>Store Manager</h3>
                    <h4>Madison Jewelers</h4>
                    <div className="date">Aug 2017 - July 2019</div>
                    <ul>
                      <li>Led and mentored a team of sales associates, providing training and guidance to enhance their product knowledge and sales skills, fostering a cohesive and high-performing team environment</li>
                      <li>Managed approximately 2.5 million dollars in inventory and track monthly, quarterly, and yearly budget expenses</li>
                      <li>Performed annual performance assessments, organized work assignments and schedules</li>
                      <li>Collaborated with marketing teams to develop and implement targeted promotional campaigns, significantly increasing customer engagement and foot traffic to the store, driving sales growth</li>
                    </ul>
                    
                    <h4>Salesperson</h4>
                    <div className="date">Sep 2011 - Aug 2017</div>
                    <ul>
                      <li>Exceeded sales targets, cultivating, and managing a dedicated client base, resulting in a remarkable 30% increase in revenue within the first year</li>
                      <li>Demonstrated exceptional product knowledge and effectively conveyed the unique qualities and value of fine jewelry and luxury watches, ensuring informed purchase decisions for customers</li>
                    </ul>
                  </TimelineContent>
                </TimelineItem>
              </TimelineContainer>
            </ScrollObserver>
          </ResumeContainer>
        </>
      )}
    </PageContainer>
  );
};

export default Resume;